import type { Distance, Margins, Rectangle, SpatialRectangle } from "@pdfking/math"
import {
  AddMargins,
  Outline,
  RemoveMargins,
  Round,
  Subtract,
  SumMargins,
  ToSpatial,
} from "@pdfking/math"
import { ZERO_DEGREE } from "@pdfking/math/constants"
import { SortBy } from "@pdfking/tfunc"

import type { ResolvedNode, SidedLayout, SidedPage } from "#public"

// The leaf itself, not the package's index: that one reaches back in here.
import { TurnMargins } from "../../public/functions/TurnMargins"
import type { Container } from "../types/flex"
import { FlexEnum } from "../types/flex"
import type { Alloted } from "./allot"
import { computeLayout } from "./flex"

/** A page as it was laid out, before it is told apart from its bleed. */
type Placed<D> = {
  block: number
  bled: SpatialRectangle<Distance>
  /** The page's bleed on the sheet's edges. */
  bleed: Margins<Distance>
  page: SidedPage<D>
}

/**
 * Where every page of a resolved tree lands: `root`
 * is the whole fill — its box the sheet, its padding the security border —
 * already resolved, spending its leftover the way the filling asks, and
 * {@link allot} shares the sheet out as each page's bleed.
 *
 * The tree lays out y-down and the plot grows y-up, so every box is flipped
 * once on the way out. Nothing is mirrored: a symmetric fill holds its other
 * half as flipped pages of its own, rear side up.
 */
export const flex_layout = <D>(root: Container<D> & Rectangle<Distance>): SidedLayout<D> => {
  const frame = ToSpatial(root)

  const upright = (rect: SpatialRectangle<Distance>): SpatialRectangle<Distance> => ({
    ...rect,
    y: (frame.height - rect.y - rect.height) as Distance,
  })

  // The cell is the subject plus its padding. The subject is the cell minus
  // that padding — turned with the cell — and what the padding gave up is
  // bleed the page owns.
  const place = (cell: Alloted<D>): Placed<D> => {
    if (cell.item.enum !== FlexEnum.Page) throw new Error("a page is a leaf")
    const padding = TurnMargins(cell.item.padding, cell.item.rotation)
    const box = upright(cell.rect)
    const position = RemoveMargins(box, padding)
    // What the sheet gave the page around it: its share, plus the padding it
    // gave up. Sheet frame here, since that is where the boxes are compared;
    // the page carries it turned back into its own.
    const bleed = SumMargins(cell.bleed, padding)
    return {
      bled: AddMargins(position, bleed),
      bleed,
      block: cell.item.block,
      page: {
        bleed: TurnMargins(bleed, Subtract(ZERO_DEGREE, cell.item.rotation)),
        block: cell.item.block,
        data: cell.item.data,
        guid: cell.item.guid,
        is_front: cell.item.is_front,
        padding: cell.item.padding,
        position,
        rotation: cell.item.rotation,
      },
    }
  }

  const placed: Placed<D>[] = []
  const node = (alloted: Alloted<D>): ResolvedNode<D> => {
    if (alloted.item.enum === FlexEnum.Page) {
      const page = place(alloted)
      placed.push(page)
      return {
        bleed: page.bleed,
        children: [],
        data: alloted.item.data,
        is_front: alloted.item.is_front,
        padding: alloted.item.padding,
        page: page.page,
        position: page.page.position,
        rotation: alloted.item.rotation,
      }
    }
    return {
      bleed: alloted.bleed,
      children: alloted.children.map(node),
      data: alloted.item.data,
      is_front: alloted.item.is_front,
      padding: alloted.item.padding,
      page: undefined,
      position: upright(alloted.rect),
      rotation: alloted.item.rotation,
    }
  }

  // The security border is nobody's until a page bleeds into it, and the
  // outermost ones do: the whole sheet is shared out.
  const tree = node(computeLayout(root, frame))

  // The tree emits line by line, and the turned strip first when it leads, so
  // the pages come out in the order they were laid, not the order they are
  // numbered: block by block, column by column, top to bottom.
  return {
    mirror: undefined,
    pages: SortBy(placed, p => [p.block, Round(p.page.position.x), -Round(p.page.position.y)]).map(
      p => p.page
    ),
    position: Outline(placed.map(p => p.page.position)),
    root: tree,
    security: RemoveMargins(frame, root.padding),
  }
}
